import React, { useEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Stack,
  Pagination,
} from '@mui/material';
import adCenterApi from '../adCenterApi';
import { useNavigate } from 'react-router-dom';

export default function BillingHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // 🔹 Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10); // You can make this selectable if needed
  const [totalPages, setTotalPages] = useState(1);

  const fetchBillingDetails = async (pageNumber) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adCenterApi.post('/getBillingDetailsByBroker', {
        adminUser: localStorage.getItem('uuid'),
        startDate: '',
        endDate: '',
        offset: (pageNumber - 1) * rowsPerPage + 1, // API expects page number here
        limit: rowsPerPage,
      });

      if (response.data && response.data.statusCode === 0) {
        setHistory(response.data.billingDetails || []);

        // 🔹 If API provides total records, calculate total pages
        if (response.data.totalCount) {
          setTotalPages(Math.ceil(response.data.totalCount / rowsPerPage));
        } else {
          // fallback: if no totalCount in API response
          setTotalPages(5);
        }
      } else {
        setError(response.data.message || 'Failed to fetch billing details');
      }
    } catch (err) {
      setError('Error fetching billing details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingDetails(page);
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
              Billing History
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              mt={0.5}
              sx={{ fontStyle: 'italic' }}
            >
              Your campaign spends are deducted daily from your Settlement wallet balance
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Card elevation={6} sx={{ borderRadius: 3, boxShadow: 6 }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              {loading ? (
                <Stack spacing={2}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
                  ))}
                </Stack>
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#222b36' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#fff', borderBottom: 'none' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#fff', display: 'flex', alignItems: 'center', borderBottom: 'none' }}>
                            Amount
                            <Tooltip
                              title="The total sum owed from all your campaigns are deducted on a weekly basis from your Settlement wallet balance."
                              arrow
                            >
                              <InfoOutlinedIcon
                                sx={{ ml: 1, fontSize: 18, color: '#b0b8c1', verticalAlign: 'middle', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#fff', borderBottom: 'none' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No history found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          history.map((row, idx) => (
                            <TableRow
                              key={row.transactionDate || idx}
                              hover
                              sx={{
                                transition: 'background 0.2s',
                                '&:hover': { backgroundColor: 'grey.100' },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 500 }}>{row.transactionDate}</TableCell>
                              <TableCell>{row.amount}</TableCell>
                              <TableCell>{row.status}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* 🔹 Pagination Component */}
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="medium"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
}
